export const NOTIFICATION_EVENT_ACTION_ALL = 'all';

const meta = (label, group, variant, en, tr) => ({
    label,
    group,
    variant,
    endings: { en: Array.isArray(en) ? en : [en], tr: Array.isArray(tr) ? tr : [tr] },
});

const ACTION_META = Object.freeze({
    applicantReceipt: meta('RECEIPT', 'lifecycle', 'success', 'applicant receipt sent', 'başvuru alındı'),
    approved: meta('APPROVE', 'lifecycle', 'success', 'approved', 'onaylandı'),
    assigned: meta('ASSIGN', 'lifecycle', 'info', 'assigned', 'atandı'),
    biaUnlinked: meta('UNLINK BIA', 'relationship', 'neutral', 'unlinked', 'bağlantısı kaldırıldı'),
    biasLinked: meta('LINK BIA', 'relationship', 'info', 'linked', 'bağlandı'),
    cancelled: meta('CANCEL', 'lifecycle', 'destructive', 'cancelled', 'iptal edildi'),
    closed: meta('CLOSE', 'lifecycle', 'neutral', 'closed', 'kapatıldı'),
    completed: meta('COMPLETE', 'lifecycle', 'success', 'completed', 'tamamlandı'),
    contractExpiryReminder: meta('CONTRACT EXPIRY', 'deadline', 'warning', 'contract expiry approaching', 'sözleşme bitişi yaklaşıyor'),
    created: meta('CREATE', 'change', 'success', 'created', 'oluşturuldu'),
    deadlineOverdue: meta('DEADLINE OVERDUE', 'deadline', 'destructive', 'deadline overdue', 'süre aşımı'),
    deadlineReminder: meta('DEADLINE REMINDER', 'deadline', 'warning', ['deadline reminder', 'expiry reminder'], 'süre hatırlatması'),
    delegated: meta('DELEGATE', 'lifecycle', 'info', ['approval delegated', 'delegated'], ['onayı devredildi', 'devredildi']),
    deleted: meta('DELETE', 'change', 'destructive', 'deleted', 'silindi'),
    expired: meta('EXPIRE', 'deadline', 'destructive', 'expired', 'süresi doldu'),
    expiryReminder: meta('EXPIRY REMINDER', 'deadline', 'warning', 'expiry approaching', 'süresi yaklaşıyor'),
    invited: meta('INVITE', 'lifecycle', 'info', 'invited', 'davet edildi'),
    linked: meta('LINK', 'relationship', 'info', 'linked', 'bağlandı'),
    login: meta('LOGIN', 'lifecycle', 'info', 'logged in', 'giriş yaptı'),
    objectiveUnlinked: meta('UNLINK OBJECTIVE', 'relationship', 'neutral', 'unlinked', 'bağlantısı kaldırıldı'),
    objectivesLinked: meta('LINK OBJECTIVES', 'relationship', 'info', 'linked', 'bağlandı'),
    passwordChanged: meta('PASSWORD CHANGE', 'lifecycle', 'warning', 'password changed', 'parolası değişti'),
    pending: meta('PENDING', 'lifecycle', 'warning', 'pending changes', 'bekleyen değişiklik'),
    pendingDeleteApproved: meta('DELETE APPROVED', 'lifecycle', 'success', 'pendingDeleteApproved', 'silme onayı'),
    pendingDeleteRejected: meta('DELETE REJECTED', 'lifecycle', 'destructive', 'pendingDeleteRejected', 'silme reddi'),
    pendingStatusRejected: meta('STATUS REJECTED', 'lifecycle', 'destructive', 'pendingStatusRejected', 'durum değişikliği reddi'),
    periodicDue: meta('PERIODIC DUE', 'deadline', 'warning', 'periodic disposal due', 'periyodik imha dönemi'),
    permissionsChanged: meta('PERMISSIONS CHANGE', 'lifecycle', 'warning', 'permissions changed', 'izinleri değişti'),
    plannedReminder: meta('PLANNED REMINDER', 'deadline', 'warning', 'planned date approaching', 'planı yaklaşıyor'),
    published: meta('PUBLISH', 'lifecycle', 'success', 'published', 'yayınlandı'),
    recordsExpired: meta('RECORDS EXPIRED', 'deadline', 'warning', 'expired records found', 'saklama süresi dolan kayıtlar'),
    registered: meta('REGISTER', 'lifecycle', 'success', 'registered', 'kaydoldu'),
    rejected: meta('REJECT', 'lifecycle', 'destructive', 'rejected', 'reddedildi'),
    resolved: meta('RESOLVE', 'lifecycle', 'success', 'resolved', 'çözüldü'),
    returned: meta('RETURN', 'lifecycle', 'warning', 'returned', 'iade edildi'),
    reviewReminder: meta('REVIEW REMINDER', 'deadline', 'warning', 'review approaching', 'inceleme yaklaşıyor'),
    slaBreached: meta('SLA BREACH', 'deadline', 'destructive', ['SLA breached', 'SLA breach'], ['SLA süresini aştı', 'SLA ihlali']),
    slaEscalated: meta('SLA ESCALATION', 'deadline', 'warning', 'SLA escalation', 'SLA eskalasyonu'),
    slaWarning: meta('SLA WARNING', 'deadline', 'warning', 'SLA warning', 'SLA uyarısı'),
    sscDeadlineOverdue: meta('SSC OVERDUE', 'deadline', 'destructive', 'SSC board notification overdue', 'SSC Kurul bildirim süresi aşıldı'),
    sscDeadlineReminder: meta('SSC REMINDER', 'deadline', 'warning', 'SSC board notification reminder', 'SSC Kurul bildirim hatırlatması'),
    submitted: meta('SUBMIT', 'lifecycle', 'info', 'submitted', ['onaya gönderildi', 'gönderildi']),
    supportEndReminder: meta('SUPPORT END', 'deadline', 'warning', 'support end approaching', 'destek bitişi yaklaşıyor'),
    unlinked: meta('UNLINK', 'relationship', 'neutral', 'unlinked', 'bağlantısı kaldırıldı'),
    updated: meta('UPDATE', 'change', 'info', 'updated', 'güncellendi'),
});

const GROUP_ORDER = Object.freeze(['change', 'lifecycle', 'deadline', 'relationship', 'other']);

export const INBOX_EVENT_GROUP_ORDER = Object.freeze([
    'lifecycle',
    'deadline',
    'change',
    'relationship',
    'other',
]);

function localeKey(language) {
    return String(language || '').toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

function humanizeIdentifier(value) {
    const words = String(value || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    return words.map((word, index) => {
        if (/^[A-Z0-9]{2,}$/.test(word)) return word;
        const lower = word.toLocaleLowerCase('en');
        return index === 0 ? `${lower.charAt(0).toUpperCase()}${lower.slice(1)}` : lower;
    }).join(' ');
}

function fallbackSubject(eventName) {
    return String(eventName || '').split('.').slice(0, -1).map(humanizeIdentifier).filter(Boolean).join(' · ');
}

function localizedLabel(definition, language) {
    const label = definition?.label;
    if (typeof label === 'string') return label;
    const locale = localeKey(language);
    return label?.[locale] || label?.en || label?.tr || '';
}

function stripActionEnding(label, endings, language) {
    const locale = localeKey(language);
    const normalized = String(label || '').toLocaleLowerCase(locale);
    for (const ending of [...(endings || [])].sort((left, right) => right.length - left.length)) {
        const normalizedEnding = ending.toLocaleLowerCase(locale);
        if (normalized === normalizedEnding) return '';
        if (normalized.endsWith(` ${normalizedEnding}`)) return label.slice(0, -ending.length).trim();
    }
    return null;
}

export function notificationEventAction(eventName) {
    return String(eventName || '').split('.').filter(Boolean).at(-1) || '';
}

export function knownNotificationEventActions() {
    return Object.keys(ACTION_META);
}

export function actionsInNotificationEventGroup(group) {
    return Object.entries(ACTION_META)
        .filter(([, meta]) => meta.group === group)
        .map(([action]) => action)
        .sort((left, right) => left.localeCompare(right, 'en'));
}

export function notificationEventGroup(eventName) {
    return notificationEventActionPresentation(eventName).group;
}

export function notificationEventActionPresentation(eventName) {
    const action = notificationEventAction(eventName);
    const known = ACTION_META[action];
    return {
        action,
        label: known?.label || humanizeIdentifier(action).toLocaleUpperCase('en'),
        group: known?.group || 'other',
        variant: known?.variant || 'neutral',
        endings: known?.endings || { en: [], tr: [] },
    };
}

/**
 * Inbox-facing action label (Assigned / Atandı). Catalog UIs keep `presentation.label`.
 */
export function localizedNotificationEventActionLabel(eventName, language = 'en') {
    const presentation = notificationEventActionPresentation(eventName);
    const locale = localeKey(language);
    const ending = presentation.endings?.[locale]?.[0];
    if (!ending) return presentation.label;
    return `${ending.charAt(0).toLocaleUpperCase(locale)}${ending.slice(1)}`;
}

export function notificationEventSubject(definition, language = 'en') {
    const presentation = notificationEventActionPresentation(definition?.name);
    const label = localizedLabel(definition, language);
    const stripped = stripActionEnding(label, presentation.endings[localeKey(language)], language);
    if (stripped) {
        return /[a-z0-9][A-Z]/.test(stripped) || !/\s/.test(stripped)
            ? humanizeIdentifier(stripped)
            : stripped;
    }
    return fallbackSubject(definition?.name) || label || definition?.name || '';
}

export function buildNotificationEventActionGroups(definitions = []) {
    const counts = new Map();
    for (const definition of definitions || []) {
        const action = notificationEventAction(definition?.name);
        if (action) counts.set(action, (counts.get(action) || 0) + 1);
    }
    const groups = new Map(GROUP_ORDER.map((group) => [group, []]));
    for (const [action, count] of counts) {
        const presentation = notificationEventActionPresentation(action);
        groups.get(presentation.group).push({ value: action, label: presentation.label, count });
    }
    return GROUP_ORDER.map((group) => ({
        id: group,
        options: groups.get(group).sort((left, right) => left.label.localeCompare(right.label, 'en')),
    })).filter(({ options }) => options.length > 0);
}
